import type { AppearanceRow } from '@setplex/tria-api'
import type { ComponentsPage, Row, TGetComponentsFx } from './index.h'

import { match } from 'variant'
import { CONTENT } from '~/features/appearance/components'
import { component, HOME_BANNERS } from '../../ui/lib/component'
import { Dirty, Empty, Loading, Updating } from './index.h'

//
// row transition helpers
//

/**
 * Change row to dirty state
 */
export function toDirty(row: Row): Row {
  const { refreshable } = row.desc
  return {
    ...row,
    data: match(row.data, {
      Empty: (data) => data, // leave as is
      Loading: (data) => (refreshable ? Empty() : data), // if refreshable, drop loading state and reset
      Failed: () => Empty(), // reset failed state -> try to reload
      Dirty: (data) => data, // leave as is
      Updating: (data) => (refreshable ? Empty() : data), // if refreshable, drop updating state and reset
      Awaiting: (data) => (refreshable ? Dirty(data.payload) : data), // if refreshable, set dirty
      Ready: (data) => (refreshable ? Dirty(data.payload) : data), // if refreshable, set dirty
    }),
  }
}

/**
 * Change row to loading state
 */
export function toLoading(row: Row): Row {
  return {
    ...row,
    data: match(row.data, {
      Empty: () => Loading(),
      Loading: (data) => data, // leave as is
      Failed: () => Loading(),
      Dirty: (data) => Updating(data.payload),
      Updating: (data) => data, // leave as is
      Awaiting: (data) => Updating(data.payload), // do not erase!
      Ready: (data) => Updating(data.payload), // do not erase!
    }),
  }
}

export const filterRowsVisibility = (
  components: ComponentsPage,
  source: TGetComponentsFx['source']
) => {
  const { modules, features, options, isGuest, appearanceModules, identifire } =
    source

  /*
   * If appearanceCustomizationEnabled = true but HOME is static module,
   * thats old nora version,
   * and it would not return banners as module,
   * so insert them manually.
   */
  let content = components.content
  if (identifire === 'home' && components.first) {
    const homeModule = appearanceModules?.find(
      ({ identifier }) => identifier === 'home'
    )
    const isBannersMissing =
      components.content[0].contentType !== CONTENT.BANNER_CAROUSEL

    if (homeModule && isBannersMissing && homeModule.type === 'static') {
      content = [component(HOME_BANNERS, 1), ...components.content]
    }
  }

  // filter rows (default and custom both)
  return content.filter((comp, index) => {
    // filter rows, disabled by flags

    const localizedTitleKey = (comp as AppearanceRow).localizedTitleKey
    const contentType = comp.contentType

    // prettier-ignore
    if (
      (contentType === 'TV_CHANNEL' && !modules.tvEnabled) ||
      (contentType === 'TV_BUNDLE' && !modules.tvEnabled) ||
      ((contentType === 'TV_SHOW' || contentType === 'MOTN_TV_SHOW') && !modules.tvShowEnabled) ||
      (contentType === 'TV_SHOW_BUNDLE' && !modules.tvShowEnabled) ||
      ((contentType === 'VOD' || contentType === 'MOTN_VOD') && !modules.vodEnabled) ||
      (contentType === 'VOD_BUNDLE' && !modules.vodEnabled) ||
      (contentType === 'LIVE_EVENT' && !modules.liveEventEnabled) ||
      (contentType === 'PARTNER_PRODUCT' && !features.partnersProgramEnabled) ||
      (contentType === 'BANNER_CAROUSEL' && (index !== 0 || !options.bannerCarouselsEnabled)) ||
      (localizedTitleKey === 'movies_featured' && !options.featuredCarouselsEnabled) ||
      (localizedTitleKey === 'series_featured' && !options.featuredCarouselsEnabled) ||
      (localizedTitleKey === 'channel_featured' && !options.featuredCarouselsEnabled)
    ) return false

    // filter rows, unavailable for guests
    // prettier-ignore
    if (isGuest &&
       (localizedTitleKey === 'continue_watching' ||
        localizedTitleKey === 'live_channels_recently_watched' ||
        localizedTitleKey === 'channel_favorites' ||
        localizedTitleKey === 'movies_favorite' ||
        localizedTitleKey === 'series_favorite')
    ) return false

    return true
  })
}
